/**
 * In-browser editing (ADR-0021). Opens OnlyOffice Docs on the author's draft.
 * Saves go straight back to DocVault as new working uploads of the draft; the
 * author still submits it for approval from the document page as usual.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useStore } from "../lib/store";
import { Loading } from "../components/bits";

declare global {
  interface Window { DocsAPI?: { DocEditor: new (id: string, config: Record<string, unknown>) => { destroyEditor?: () => void } } }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.DocsAPI) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("The editor server didn't answer. It may be stopped, or still starting up."));
    document.head.appendChild(s);
  });
}

export function EditorPage() {
  const { id = "" } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { reload } = useStore();
  const [err, setErr] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const editor = useRef<{ destroyEditor?: () => void } | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // one session per visit (React strict mode runs effects twice)
    started.current = true;
    (async () => {
      try {
        const s = await api.editorSession(id, sp.get("notes") ?? undefined);
        await loadScript(`${s.server_url}/web-apps/apps/api/documents/api.js`);
        editor.current = new window.DocsAPI!.DocEditor("oo-editor", { ...s.config, width: "100%", height: "100%" });
        setReady(true);
      } catch (e) { setErr((e as Error).message); }
    })();
    return () => { try { editor.current?.destroyEditor?.(); } catch { /* already gone */ } };
  }, [id]);

  const done = async () => { await reload(); nav(`/doc/${id}`); };
  return (
    <div className="view editor-view">
      <div className="editor-bar">
        <div><b>Editing in the browser</b><span className="cap"> · changes save to your draft automatically. Submit it for approval from the document page when you're done.</span></div>
        <button className="btn btn-accent btn-sm" onClick={() => void done()}>Done editing</button>
      </div>
      {err ? (
        <div className="card empty"><h3>The editor didn't open</h3>{err}<div style={{ marginTop: 12 }}><Link to={`/doc/${id}`}>Back to the document</Link></div></div>
      ) : (
        <div className="editor-frame">{!ready && <Loading label="Opening the editor…" />}<div id="oo-editor" /></div>
      )}
    </div>
  );
}
